import { t } from "i18next";
import { useAtomValue } from "jotai/react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { CloseIcon } from "~/components/ui/icons/close";
import { ExternalLinkDefaultIcon } from "~/components/ui/icons/external-link-default";
import { amDefaultContact, findContact } from "~/stores/choose-contact";
import { activeSaleState } from "~/stores/sales";
import { AmContact } from "~/views/billing/am-contact";

const usefulLinks = [
  {
    label: t("contact_us_dialog.return_block_approve_refund"),
    en: "https://app.notion.com/p/choose/Return-Approve-Block-a-Refund-3958868d42d680528dfdc131142d45a9",
    fr: "https://app.notion.com/p/choose/Retour-Valider-bloquer-un-remboursement-3668868d42d6801e90a5f2e66b196ebd",
  },
  {
    label: t("contact_us_dialog.out_of_stock_cancellation"),
    en: "https://app.notion.com/p/choose/Out-of-Stock-Cancellation-3958868d42d680138908f85a175d80a6",
    fr: "https://app.notion.com/p/choose/Rupture-de-stock-Annulation-3668868d42d680198309e82d019042bb",
  },
  {
    label: t("contact_us_dialog.invoicing_when_how_to"),
    en: "https://app.notion.com/p/choose/Invoicing-When-How-to-Invoice-3958868d42d6809bbb3cceecd37e6146",
    fr: "https://app.notion.com/p/choose/Facturation-Quand-comment-facturer-3668868d42d680a6879edffa863acb5f",
  },
  {
    label: t("contact_us_dialog.claim_tracking_blocked"),
    en: "https://app.notion.com/p/choose/Claim-Stalled-Tracking-Package-Delivered-but-Not-Received-3958868d42d680a49369f99717f84ff5",
    fr: "https://app.notion.com/p/choose/R-clamation-Suivi-bloqu-Colis-livr-non-re-u-3668868d42d68075b601f4ee6210e7db",
  },
  {
    label: t("contact_us_dialog.claim_offer_goodwill_gesture"),
    en: "https://app.notion.com/p/choose/Claim-Offering-a-Goodwill-Gesture-3958868d42d6800a92ddc2d5ef38dc83",
    fr: "https://app.notion.com/p/choose/R-clamation-Proposer-un-geste-commercial-3668868d42d68030846bd0cf228e2183",
  },
];

export function ContactUsDialog() {
  const { i18n, t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);

  const amContact = findContact(
    activeSale?.logistics_manager ?? undefined,
    amDefaultContact,
  );

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Content
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="fixed bottom-6 right-6 z-[999] w-[426px] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg"
      >
        <DialogHeader className="border-b pb-6">
          <DialogTitle className="text-2xl">
            {t("contact_us_dialog.title")}
          </DialogTitle>
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4"
            >
              <CloseIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        </DialogHeader>
        <div className="flex flex-col gap-6 py-4 pt-6">
          <div className="rounded-lg border border-gray-100 p-6">
            <AmContact contact={amContact} from="home" />
          </div>
          <div className="flex flex-col gap-4">
            <div className="font-semibold text-gray-900">
              {t("contact_us_dialog.useful_links")}
            </div>
            <ul className="flex flex-col gap-4">
              {usefulLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={i18n.language === "fr" ? link.fr : link.en}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-green-900"
                  >
                    {link.label}
                    <ExternalLinkDefaultIcon className="mb-0.75 ml-2 inline-block size-4" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
